import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../components/api';

// Import des nouveaux composants modulaires
import ExamenPageHeader from '../components/examen/ExamenPageHeader';
import ExamenGeneralInfo from '../components/examen/ExamenGeneralInfo';
import ExamenSessionsList from '../components/examen/ExamenSessionsList';
import ExamenFormActions from '../components/examen/ExamenFormActions';

const CreateExamenPage = () => {
  const navigate = useNavigate();
  
  // États principaux
  const [loading, setLoading] = useState(false);
  const [loadingRefs, setLoadingRefs] = useState(true);
  
  // Données du formulaire
  const [formData, setFormData] = useState({
    libelle: '',
    description: '',
    classe: '',
    date_debut: '',
    date_fin: '',
    status: 'PROGRAMME'
  });

  // Sessions et créneaux (avec champ surveillant secondaire ajouté)
  const [sessions, setSessions] = useState([{
    date_examen: '',
    creneaux: [{
      heure_debut: '',
      heure_fin: '',
      module: '',
      salle: '',
      surveillant_principal: '',
      surveillant_secondaire: '',
      consignes: ''
    }]
  }]);

  // Données de référence
  const [classes, setClasses] = useState([]);
  const [modules, setModules] = useState([]);
  const [salles, setSalles] = useState([]);
  const [surveillants, setSurveillants] = useState([]); // Liste combinée enseignants + personnel

  // Chargement des données initiales
  useEffect(() => {
    fetchReferenceData();
  }, []);

  // Chargement des modules quand la classe change
  useEffect(() => {
    if (formData.classe) {
      fetchModules(formData.classe);
    }
  }, [formData.classe]);

  // Fonctions de chargement des données
  const fetchReferenceData = async () => {
    try {
      const [classesRes, sallesRes, enseignantsRes, personnelRes] = await Promise.all([
        api.get('/classe/get-all-classe/'),
        api.get('/etablissement/salles/'),
        api.get('/enseignant/liste-enseignant/'),  // URL corrigée
        api.get('/etablissement/personnels/')      // URL corrigée
      ]);

      // Classes
      setClasses(Array.isArray(classesRes.data) ? classesRes.data : []);
      
      // Salles
      console.log('Données salles reçues:', sallesRes.data);
      const sallesData = sallesRes.data;
      if (Array.isArray(sallesData)) {
        setSalles(sallesData);
      } else if (sallesData && Array.isArray(sallesData.results)) {
        setSalles(sallesData.results);
      } else if (sallesData && typeof sallesData === 'object') {
        setSalles(Object.values(sallesData));
      } else {
        setSalles([]);
      }

      // Combiner enseignants et personnel pour les surveillants
      // Gestion des différents formats de réponse API
      let enseignants = [];
      if (enseignantsRes.data) {
        if (Array.isArray(enseignantsRes.data)) {
          enseignants = enseignantsRes.data;
        } else if (enseignantsRes.data.results && Array.isArray(enseignantsRes.data.results)) {
          enseignants = enseignantsRes.data.results;
        } else if (enseignantsRes.data.data && Array.isArray(enseignantsRes.data.data)) {
          enseignants = enseignantsRes.data.data;
        }
      }
      
      let personnel = [];
      if (personnelRes.data) {
        if (Array.isArray(personnelRes.data)) {
          personnel = personnelRes.data;
        } else if (personnelRes.data.results && Array.isArray(personnelRes.data.results)) {
          personnel = personnelRes.data.results;
        } else if (personnelRes.data.data && Array.isArray(personnelRes.data.data)) {
          personnel = personnelRes.data.data;
        }
      }

      // Créer une liste combinée avec indication du type
      const surveillantsCombines = [
        ...enseignants.map(ens => ({
          id: `enseignant_${ens.id}`,
          type: 'enseignant',
          original_id: ens.id,
          nom: ens.nom,
          prenom: ens.prenom,
          label: `${ens.nom} ${ens.prenom} (Enseignant)`
        })),
        ...personnel.map(pers => ({
          id: `personnel_${pers.id}`,
          type: 'personnel', 
          original_id: pers.id,
          nom: pers.nom,
          prenom: pers.prenom,
          label: `${pers.nom} ${pers.prenom} (Personnel)`
        }))
      ];

      setSurveillants(surveillantsCombines);

    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast.error('Erreur lors du chargement des données de référence');
      // Initialiser avec des tableaux vides en cas d'erreur
      setClasses([]);
      setSalles([]);
      setSurveillants([]);
    } finally {
      setLoadingRefs(false);
    }
  };

  const fetchModules = async (classeId) => {
    try {
      const response = await api.get(`/emplois/modules-by-classe/${classeId}/`);
      setModules(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur lors du chargement des modules:', error);
      setModules([]);
    }
  };

  // Handlers pour les informations générales
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handlers pour les sessions
  const handleSessionChange = (sessionIndex, field, value) => {
    setSessions(prev => {
      const updated = [...prev];
      updated[sessionIndex] = { ...updated[sessionIndex], [field]: value };
      return updated;
    });
  };

  const handleAddSession = () => {
    setSessions(prev => [...prev, {
      date_examen: '',
      creneaux: [{
        heure_debut: '',
        heure_fin: '',
        module: '',
        salle: '',
        surveillant_principal: '',
        surveillant_secondaire: '',
        consignes: ''
      }]
    }]);
  };

  const handleRemoveSession = (index) => {
    if (sessions.length > 1) {
      setSessions(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Handlers pour les créneaux
  const handleCreneauChange = (sessionIndex, creneauIndex, field, value) => {
    setSessions(prev => {
      const updated = [...prev];
      updated[sessionIndex].creneaux[creneauIndex] = {
        ...updated[sessionIndex].creneaux[creneauIndex],
        [field]: value
      };
      return updated;
    });
  };

  const handleAddCreneau = (sessionIndex) => {
    setSessions(prev => {
      const updated = [...prev];
      updated[sessionIndex].creneaux.push({
      heure_debut: '',
      heure_fin: '',
      module: '',
      salle: '',
      surveillant_principal: '',
        surveillant_secondaire: '',
      consignes: ''
      });
      return updated;
    });
  };

  const handleRemoveCreneau = (sessionIndex, creneauIndex) => {
    setSessions(prev => {
      const updated = [...prev];
      if (updated[sessionIndex].creneaux.length > 1) {
        updated[sessionIndex].creneaux.splice(creneauIndex, 1);
      }
      return updated;
    });
  };

  // Validation du formulaire
  const validateForm = () => {
    // Validation des informations générales
    if (!formData.libelle.trim()) {
      toast.error('Le libellé est requis');
      return false;
    }
    if (!formData.classe) {
      toast.error('La classe est requise');
      return false;
    }
    if (!formData.date_debut || !formData.date_fin) {
      toast.error('Les dates de début et fin sont requises');
      return false;
    }
    if (new Date(formData.date_fin) < new Date(formData.date_debut)) {
      toast.error('La date de fin doit être postérieure à la date de début');
      return false;
    }

    // Validation des sessions et créneaux
    for (let i = 0; i < sessions.length; i++) {
      const session = sessions[i];
      if (!session.date_examen) {
        toast.error(`Session ${i + 1}: La date est requise`);
        return false;
      }
      
      for (let j = 0; j < session.creneaux.length; j++) {
        const creneau = session.creneaux[j];
        if (!creneau.heure_debut || !creneau.heure_fin || !creneau.module) {
          toast.error(`Session ${i + 1}, Créneau ${j + 1}: Les heures et le module sont obligatoires`);
          return false;
        }
        if (creneau.heure_fin <= creneau.heure_debut) {
          toast.error(`Session ${i + 1}, Créneau ${j + 1}: L'heure de fin doit être après l'heure de début`);
          return false;
        }
      }
    }

    return true;
  };

  // Fonction pour transformer les surveillants pour l'API
  const transformSurveillantForAPI = (surveillantId) => {
    if (!surveillantId) return null;
    
    const surveillant = surveillants.find(s => s.id === surveillantId);
    if (!surveillant) return null;

    return {
      type: surveillant.type,
      id: surveillant.original_id
    };
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Transformer les sessions avec créneaux en sessions individuelles pour le backend
      const backendSessions = [];
      sessions.forEach(session => {
        session.creneaux.forEach(creneau => {
          const sessionData = {
            date_examen: session.date_examen,
            heure_debut: creneau.heure_debut,
            heure_fin: creneau.heure_fin,
            module: creneau.module,
            salle: creneau.salle,
            consignes: creneau.consignes
          };

          // Traitement des surveillants
          if (creneau.surveillant_principal) {
            const surveillantPrincipal = transformSurveillantForAPI(creneau.surveillant_principal);
            if (surveillantPrincipal) {
              sessionData.surveillant_principal_type = surveillantPrincipal.type;
              sessionData.surveillant_principal_id = surveillantPrincipal.id;
            }
          }

          if (creneau.surveillant_secondaire) {
            const surveillantSecondaire = transformSurveillantForAPI(creneau.surveillant_secondaire);
            if (surveillantSecondaire) {
              sessionData.surveillant_secondaire_type = surveillantSecondaire.type;
              sessionData.surveillant_secondaire_id = surveillantSecondaire.id;
            }
          }

          backendSessions.push(sessionData);
        });
      });

      const payload = { ...formData, sessions: backendSessions };
      const response = await api.post('/examen/examens/', payload);
      
      if (response.data.status === 'success') {
        toast.success('Examen créé avec succès');
        navigate('/examens');
      } else {
        toast.error(response.data.message || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  // Calculs des statistiques
  const totalSessions = sessions.length;
  const totalCreneaux = sessions.reduce((total, session) => total + session.creneaux.length, 0);
  const isFormValid = formData.libelle && formData.classe && totalSessions > 0 && totalCreneaux > 0;

  // Affichage du loader pendant le chargement initial
  if (loadingRefs) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Header de la page */}
            <ExamenPageHeader
              totalSessions={totalSessions}
              totalCreneaux={totalCreneaux}
            />

            {/* Informations générales */}
            <ExamenGeneralInfo
              formData={formData}
              onInputChange={handleInputChange}
              classes={classes}
              loading={loading}
            />

          {/* Sessions d'examen */}
            <ExamenSessionsList
              sessions={sessions}
              modules={modules}
              salles={salles}
              surveillants={surveillants}
              onSessionChange={handleSessionChange}
              onCreneauChange={handleCreneauChange}
              onAddSession={handleAddSession}
              onAddCreneau={handleAddCreneau}
              onRemoveCreneau={handleRemoveCreneau}
              onRemoveSession={handleRemoveSession}
            />

            {/* Actions du formulaire */}
            <ExamenFormActions
              onCancel={() => navigate('/examens')}
              onSubmit={handleSubmit}
              loading={loading}
              isValid={isFormValid}
            />
          </form>
          </div>
      </div>
    </Layout>
  );
};

export default CreateExamenPage;
